import React from 'react';
import { Alert, Pane } from 'evergreen-ui';
import { ErrorDetails } from 'error/context/GlobalErrorContext';
import { GraphQLError } from 'graphql';
import { ApolloError } from '@apollo/client';

interface ErrorDisplayProps {
    error?: ErrorDetails | GraphQLError | ApolloError | string | null;
    title?: string;
    className?: string;
    children?: React.ReactNode;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
    error,
    title = 'Error',
    className,
    children
}) => {
    const loggedErrorRef = React.useRef<string | null>(null);
    
    if (!error) {
        loggedErrorRef.current = null;
        return null;
    }

    const getErrorMessage = (err: any): string => {
        if (typeof err === 'string') {
            return err;
        } else if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
            return err.graphQLErrors
                .map((e: any) => e?.extensions?.message || e.message)
                .filter(Boolean)
                .join('; ');
        } else if (err?.networkError?.message) {
            return err.networkError.message;
        } else if (err?.message) {
            return err.message;
        } else {
            return 'An error occurred';
        }
    };

    const errorMessage = getErrorMessage(error);
    
    // Log error once per unique error message for Sentry
    if (errorMessage !== loggedErrorRef.current) {
        console.error(error);
        loggedErrorRef.current = errorMessage;
    }

    return (
        <Pane className={className}>
            <Alert intent="danger" title={title}>
                {errorMessage}
                {children && (
                    <Pane marginTop={8}>
                        {children}
                    </Pane>
                )}
            </Alert>
        </Pane>
    );
};